<template>
  <div class="h-full flex flex-col overscroll-x-contain" ref="target">
    <!-- ヘッダー部 -->
    <div class="w-full bg-white border-b-[1px] border-gray200">
      <div class="flex w-full items-center h-[47px] pl-5 pr-9">
        <!-- 戻るボタン -->
        <img
          src="@/assets/Icon_Left_grayV2.svg"
          class="w-6 h-6"
          @click="goToBackPage()"
        />
        <!-- 投稿者ID -->
        <div class="w-full text-W7 text-[15px] leading-[21px] truncate">
          {{ selectComment.contributorId }}
        </div>
      </div>
    </div>
    <div name="comment-body" class="mt-3 mb-[64px]">
      <div class="flex mx-5">
        <div
          class="flex items-center min-w-0"
          @click="goToOtherUserMyPage(selectComment.contributorId)"
        >
          <img
            :src="getUserIcon"
            @error="guestImgOnError"
            class="h-8 w-8 rounded-[16px] shadow-icon mr-4"
          />
          <!-- 投稿者ID -->
          <div
            name="comment-contributorId"
            class="text-W4 text-[11px] leading-[11px] text-gray400 mt-1 text-left min-w-0 break-words"
          >
            {{ selectComment.contributorId }}
          </div>
        </div>
        <div class="flex-1" />
      </div>
      <!-- 地点情報 -->
      <div class="mt-4 mx-5 flex">
        <BaseBox
          class="border-[1px] border-gray300 w-full p-4 !rounded-[8px]"
          @click="goToSpotDetail(selectComment.spot)"
        >
          <template v-slot:explanation>
            <div class="flex-1 flex w-full items-center">
              <img
                src="@/assets/Icon_Map_Place.svg"
                class="w-3 h-3 mr-2 mt-[1px] mb-auto"
              />
              <div class="flex-1 pr-3 w-full overflow-hidden">
                <div
                  name="comment-spot-name"
                  class="text-W7 text-[13px] leading-[13px] truncate mb-1"
                >
                  {{ selectComment?.spot?.name }}
                </div>
                <div
                  name="comment-spot-address"
                  class="text-W4 text-[11px] leading-[11px] truncate text-gray400 truncate"
                >
                  {{ selectComment?.spot?.address }}
                </div>
              </div>
              <img class="h-3 w-3" src="@/assets/Icon_Right_gray.svg" />
            </div>
          </template>
        </BaseBox>
      </div>
      <!-- 投稿画像 -->
      <BaseCarouselForImage
        name="dokoiku-carousel"
        ref="carousel"
        class="mt-4"
        :displayPageCount="true"
        :displayNavigator="true"
        :images="selectComment.imgs"
      />
      <div class="mx-5 mt-4">
        <!-- 記事タイトル(2行まで表示) -->
        <div class="break-words text-left">
          <div class="text-W7 text-[15px] leading-[19px] text-gray600">
            {{ selectComment.title }}
          </div>
        </div>
        <!-- 投稿本文 -->
        <div
          name="comment-body"
          class="text-left text-W4 text-15px] whitespace-pre-line break-words mt-4"
        >
          {{ selectComment.body }}
        </div>
        <!-- URL -->
        <div
          v-if="hasCommentUrl"
          class="flex text-W4 text-[11px] leading-[7px] mt-2.5"
        >
          <img
            src="@/assets/common/Icon_Url_Head.svg"
            class="h-2 w-4 mt-0.5 mr-2"
          />
          <div
            class="text-W4 text-primary text-[11px] text-left break-all"
            @click="openUrl()"
          >
            {{ selectComment.url }}
          </div>
        </div>
        <!-- タグ -->
        <div class="flex mt-4">
          <div class="flex-1 flex gap-2 flex-wrap w-full">
            <div
              v-for="tag in selectComment.tags"
              :key="tag"
              class="w-fit max-w-full"
            >
              <BaseTag
                name="comment-tag"
                :tagName="tag"
                @click-tag="goToTagSearchResult(tag)"
              />
            </div>
          </div>
        </div>
        <!-- 投稿日 -->
        <div
          name="comment-createAt"
          class="mt-4 text-right text-W4 text-[11px] leading-[11px] text-gray400"
        >
          {{ getFormatYearDateSlash(selectComment.createAt) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {navigateTo, goBackDokoiku} from '@/router'
import Header from '@/components/organisms/Header.vue'
import BaseTag from '@/components/atoms/BaseTag.vue'
import BaseCarouselForImage from '@/components/atoms/BaseCarouselForImage.vue'
import Util from '@/mixins/util'
import NativeUtil from '@/mixins/nativeUtil.js'
import BaseBox from '@/components/atoms/BaseBox.vue'

const CommentDetail = {
  name: 'CommentDetail',
  components: {Header, BaseTag, BaseBox, BaseCarouselForImage},
  data() {
    return {}
  },
  computed: {
    /** 選択した投稿情報 */
    selectComment: {
      get() {
        return this.$store.state.DokoikuStore.commentDetail.selectedComment
      },
    },
    hasCommentUrl() {
      return this.selectComment.url != ''
    },
    /** 投稿者アイコン */
    getUserIcon: {
      get() {
        // ユーザーアイコン未設定の場合、ゲストアイコンを表示
        if (this.selectComment.publisherImg == '') {
          return require('@/assets/menu/icon_guest.svg')
        }

        return this.selectComment.publisherImg
      },
    },
    /** スクロール位置 */
    scrollPosition: {
      get() {
        return this.$store.state.DokoikuStore.commentDetail.scrollPosition
      },
      set(value) {
        this.$store.commit('DokoikuStore/setCommentDetailInfo', {
          key: 'scrollPosition',
          value: value,
        })
      },
    },
  },
  mounted() {
    this.$refs.target.addEventListener('scroll', (event) => {
      this.scrollPosition = event.target.scrollTop
    })
  },
  activated() {
    // アクティブになったタイミングで、スクロール位置を元の状態に切り替える。
    this.$nextTick(() => {
      this.$refs.target.scrollTop = this.scrollPosition
    })
  },
  methods: {
    /**
     * 投稿者画像読み込み失敗時処理
     * 読み込み失敗した場合、ゲストアイコンを表示する
     * @param {Event} event イベント情報
     */
    guestImgOnError(event) {
      event.target.src = require('@/assets/Icon_Guest.svg')
    },
    /**
     * 前の画面に遷移
     */
    goToBackPage() {
      this.scrollPosition = 0 // スクロール位置を初期化
      if (this.$refs?.carousel?.init) {
        this.$refs.carousel.init() // カルーセル初期化
      }
      goBackDokoiku(this.$route.name)
    },
    /**
     * [YYYY/MM/DD]の形式で日付表記をフォーマット
     * ※ mixinsを使わない形式に今後移行する必要がある為、mixins経由ではなくimportしたモジュールを直接利用。
     * @param {String} date 日付文字列
     * @returns {String} フォーマット後の日付文字列
     */
    getFormatYearDateSlash(date) {
      return Util.methods.getFormatYearDateSlash(date)
    },
    /**
     * URLを別ブラウザで展開
     */
    async openUrl() {
      await NativeUtil.methods.openExternalUrl(this.selectComment.url)
      NativeUtil.methods.sendJumpDokoikuCommentUrlLogEventForGA(
        this.selectComment.contributorId,
        this.selectComment.commentId,
        this.selectComment.url
      )
    },
    /**
     * 他ユーザーマイページ画面に遷移
     * 選択された投稿者IDを保持する
     * @param {String} selectedContributorId 選択された投稿者ID
     */
    goToOtherUserMyPage(selectedContributorId) {
      this.$store.commit('DokoikuStore/initOtherUserMyPageInfo')
      this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
        key: 'selectedContributorId',
        value: selectedContributorId,
      })
      this.saveThisPage()
      navigateTo({name: this.$config.DISPLAY_DOKOIKU_OTHER_USER_MY_PAGE})
      NativeUtil.methods.sendViewDokoikuContributorPageLogEventForGA(
        selectedContributorId
      )
    },
    /**
     * 選択されたタグ名を保存し、タグ検索結果画面に遷移
     * @param {String} tag タグ名
     */
    goToTagSearchResult(tag) {
      this.isShowTagSearchSuggest = false // keep-aliveにより表示状態が保持されるため、明示的に非表示
      this.$store.commit('DokoikuStore/initTagSearchResultInfo')
      this.$store.commit('DokoikuStore/setTagSearchResultInfo', {
        key: 'selectTag',
        value: tag,
      })
      this.saveThisPage()
      navigateTo({name: this.$config.DISPLAY_DOKOIKU_TAG_SEARCH_RESULT})
    },
    /**
     * 選択された地点情報を保持し、地点詳細画面に遷移
     * @param {Object} spot 地点情報
     */
    goToSpotDetail(spot) {
      this.isShowTagSearchSuggest = false // keep-aliveにより表示状態が保持されるため、明示的に非表示
      this.$store.commit('DokoikuStore/initSpotDetailInfo')
      this.$store.commit('DokoikuStore/setSpotDetailInfo', {
        key: 'selectedSpot',
        value: spot,
      })
      this.saveThisPage()
      navigateTo({name: this.$config.DISPLAY_DOKOIKU_SPOT_DETAIL})
    },
    /**
     * 投稿詳細画面のデータ保存
     */
    saveThisPage() {
      this.$store.dispatch('PageControl/saveDokoikuHistoryPage', this.$route)
      if (this.$refs?.carousel?.init) {
        this.$refs.carousel.init() // カルーセル初期化
      }
    },
  },
}
export default CommentDetail
</script>

<style scooped></style>
